/**
 * Location Display Configuration
 * Shared utility for formatting location codes consistently across the application.
 * Configuration is stored in organization settings and loaded from the backend.
 */

import { api } from './api'

export type LevelDisplayConfig = {
  prefixFrom: 'warehouse' | 'zone' | 'aisle' | 'rack' | 'level' | 'none'
  separator: string
}

export type LocationDisplayConfigs = Record<string, LevelDisplayConfig>

export const DEFAULT_DISPLAY_CONFIGS: LocationDisplayConfigs = {
  zone: { prefixFrom: 'none', separator: '-' },
  aisle: { prefixFrom: 'none', separator: '-' },
  rack: { prefixFrom: 'aisle', separator: '-' },
  level: { prefixFrom: 'aisle', separator: '-' },
  bin: { prefixFrom: 'aisle', separator: '-' },
}

/**
 * Location data for formatting
 */
export interface LocationData {
  warehouse?: { code: string; name?: string } | string
  zone?: { code: string | null; name?: string | null } | string | null
  aisle?: string | null
  rack?: string | null
  level?: string | null
  bin?: string | null
}

/**
 * Get the location type based on which fields are populated
 */
export function getLocationType(loc: LocationData): 'zone' | 'aisle' | 'rack' | 'level' | 'bin' {
  if (loc.bin) return 'bin'
  if (loc.level) return 'level'
  if (loc.rack) return 'rack'
  if (loc.aisle) return 'aisle'
  return 'zone'
}

/**
 * Build a formatted location code based on display config.
 * Each level's separator defines how IT is separated from its parent.
 */
export function buildFormattedCode(
  loc: LocationData,
  config: LevelDisplayConfig,
  allConfigs: LocationDisplayConfigs = DEFAULT_DISPLAY_CONFIGS
): string {
  const levels = ['warehouse', 'zone', 'aisle', 'rack', 'level', 'bin'] as const
  const locType = getLocationType(loc)
  const locTypeIndex = levels.indexOf(locType)
  const prefixFromIndex = config.prefixFrom === 'none' ? locTypeIndex : levels.indexOf(config.prefixFrom)

  let result = ''

  // Build from prefixFrom level up to and including the current level
  for (let i = prefixFromIndex; i <= locTypeIndex; i++) {
    const levelName = levels[i]
    let value = ''
    if (levelName === 'warehouse') {
      value = typeof loc.warehouse === 'string' ? loc.warehouse : loc.warehouse?.code || ''
    } else if (levelName === 'zone') {
      value = typeof loc.zone === 'string' ? loc.zone : loc.zone?.code || ''
    } else if (levelName === 'aisle') {
      value = loc.aisle || ''
    } else if (levelName === 'rack') {
      value = loc.rack || ''
    } else if (levelName === 'level') {
      value = loc.level || ''
    } else if (levelName === 'bin') {
      value = loc.bin || ''
    }

    if (value) {
      if (result) {
        // Use this level's separator to join with previous
        const levelConfig = allConfigs[levelName]
        const separator = levelConfig?.separator ?? '-'
        result += separator + value
      } else {
        result = value
      }
    }
  }

  return result
}

const DISPLAY_CONFIGS_CACHE_KEY = 'locationDisplayConfigs'

/**
 * Get cached display configs from localStorage (synchronous)
 */
export function getCachedDisplayConfigs(): LocationDisplayConfigs {
  try {
    const cached = localStorage.getItem(DISPLAY_CONFIGS_CACHE_KEY)
    if (cached) {
      return JSON.parse(cached)
    }
  } catch {
    // Ignore parse errors
  }
  return DEFAULT_DISPLAY_CONFIGS
}

/**
 * Fetch display configs from the backend and cache to localStorage
 */
export async function fetchDisplayConfigs(): Promise<LocationDisplayConfigs> {
  try {
    const saved = await api.get<LocationDisplayConfigs | null>('/organizations/settings/location-display')
    if (saved) {
      // Ensure all levels have separator (migration from old format)
      const migrated: LocationDisplayConfigs = {}
      for (const key of Object.keys(saved)) {
        migrated[key] = {
          prefixFrom: saved[key].prefixFrom || 'none',
          separator: saved[key].separator ?? '-'
        }
      }
      // Cache to localStorage for immediate access on next load
      localStorage.setItem(DISPLAY_CONFIGS_CACHE_KEY, JSON.stringify(migrated))
      return migrated
    }
  } catch (e) {
    // Fallback to localStorage if API fails
    return getCachedDisplayConfigs()
  }
  return DEFAULT_DISPLAY_CONFIGS
}

/**
 * Format a location node for display
 */
export function formatLocationDisplay(
  type: 'zone' | 'aisle' | 'rack' | 'level' | 'bin' | string,
  metadata: LocationData,
  code: string,
  configs: LocationDisplayConfigs
): string {
  if (!['zone', 'aisle', 'rack', 'level', 'bin'].includes(type)) {
    return code // Facilities and warehouses just use their code
  }

  const config = configs[type]
  if (!config) return code

  // Build the location data from metadata
  const locData: LocationData = {
    warehouse: metadata.warehouse,
    zone: metadata.zone || (type === 'zone' ? code : undefined),
    aisle: metadata.aisle || (type === 'aisle' ? code : undefined),
    rack: metadata.rack || (type === 'rack' ? code : undefined),
    level: metadata.level || (type === 'level' ? code : undefined),
    bin: metadata.bin || (type === 'bin' ? code : undefined),
  }

  return buildFormattedCode(locData, config, configs)
}

/**
 * Simple location data interface matching common API response pattern
 */
export interface SimpleLocationData {
  zone: string | null
  aisle: string | null
  rack: string | null
  level: string | null
  bin: string | null
}

/**
 * Format a location using cached display configs.
 * This is the primary helper for formatting location codes in components.
 * Uses localStorage-cached configs for immediate synchronous access.
 *
 * @param location - Location data with zone/aisle/rack/level/bin fields
 * @returns Formatted location string or '-' if no location data
 */
export function formatLocation(location: SimpleLocationData | null | undefined): string {
  if (!location) return '-'

  const locationType = getLocationType(location)
  if (!locationType) return '-'

  const configs = getCachedDisplayConfigs()
  const config = configs[locationType]
  if (!config) return '-'

  return buildFormattedCode(location, config, configs) || '-'
}
